import type { PaymentMethod } from '@massilia/prisma-generated'
import type { BookingSessionResponse } from '@massilia/queries'

import { useAppForm } from '@massilia/features'
import {
  setAccessToken,
  useCheckoutRegisterMutation,
  useInitiateSessionCheckout,
  useVerifyPromoCode,
} from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { useNavigate } from '@tanstack/react-router'
import { differenceInMonths } from 'date-fns'
import { ChevronLeft, Loader2 } from 'lucide-react'
import { useState } from 'react'

import { BookingSidebar } from '@/components/booking/booking-sidebar'
import { TripHeader } from '@/components/booking/trip-header'
import { useAuthStore } from '@/stores/auth.store'

import { CheckoutPromoCodeSlot } from './-sections/checkout-promo-code-slot'
import { ContactInfoSection } from './-sections/contact-info-section'
import { PaymentMethodSelector } from './-sections/payment-method-selector'
import { PaymentSection } from './-sections/payment-section'
import { checkoutFormSchema } from './checkout-form.schema'

interface CheckoutComponentProps {
  prefillEmail?: string
  sessionId: string
  isLoading: boolean
  prefillPhone?: string
  session?: BookingSessionResponse
}

export function CheckoutComponent({
  prefillEmail,
  sessionId,
  isLoading,
  prefillPhone,
  session,
}: CheckoutComponentProps) {
  const navigate = useNavigate()
  const updateAuth = useAuthStore((state) => state.updateAuth)

  // État hors-form : résultat API promo
  const [promoCodeDiscount, setPromoCodeDiscount] = useState<null | number>(null)
  const [promoCodeError, setPromoCodeError] = useState<boolean>(false)

  const { isPending: isPromoCodePending, mutateAsync: verifyPromoCode } = useVerifyPromoCode()
  const { isPending: isCheckoutPending, mutateAsync: initiateCheckout } =
    useInitiateSessionCheckout(sessionId)
  const { mutateAsync: checkoutRegister } = useCheckoutRegisterMutation()

  // Données dérivées — all from session (server is source of truth)
  const flightPriceCents = session?.totalFlightPriceCents ?? 0
  const insurancePriceCents = session?.insurance?.insurancePriceCents ?? 0
  const flightPrice = flightPriceCents / 100
  const insurancePrice = insurancePriceCents / 100
  const totalPrice = flightPrice + insurancePrice
  const paymentDeadline = session?.installmentPaymentDeadline
  const isReunion = session?.departureAirportCode === 'RUN'

  const installmentAvailable = session?.installmentInfo?.available ?? false
  const minimumDeposit = installmentAvailable
    ? (session?.installmentInfo?.minimumDepositCents ?? 0) / 100
    : 0

  const isFloaDisabled =
    installmentAvailable &&
    !!session?.flightDetails?.departureDate &&
    differenceInMonths(session.flightDetails.departureDate, new Date()) > 4

  const form = useAppForm({
    defaultValues: {
      buyerEmail: prefillEmail ?? '',
      buyerPhone: prefillPhone ?? '',
      createAccount: false,
      depositAmount: minimumDeposit,
      isPayingInMultipleTimes: false,
      password: '',
      passwordConfirmation: '',
      paymentMethod: 'credit-card' as const,
      promoCode: '',
      subscribeToNotifications: false,
    },
    onSubmit: async ({ value }) => {
      const shouldPayDeposit = installmentAvailable && value.isPayingInMultipleTimes

      const tryCreateAccount = async (email: string) => {
        if (!value.createAccount || !value.password || !value.passwordConfirmation) return

        try {
          const user = await checkoutRegister({
            email: email.trim(),
            password: value.password,
            passwordConfirmation: value.passwordConfirmation,
          })

          setAccessToken(user.accessToken)
          updateAuth({
            isAuthenticated: true,
            user: {
              email: user.email,
              firstName: user.firstName,
              id: user.id,
              lastName: user.lastName,
            },
          })
        } catch {
          // Account creation failed (e.g., account already exists).
          // Do not block the checkout — the booking is already created.
        }
      }

      if (value.paymentMethod === 'credit-card') {
        const result = await initiateCheckout({
          buyerEmail: value.buyerEmail.trim(),
          buyerPhone: value.buyerPhone?.trim() || undefined,
          depositAmount: shouldPayDeposit ? value.depositAmount : undefined,
          paymentMethod: 'CREDIT_CARD' as PaymentMethod,
          paymentType: shouldPayDeposit ? 'DEPOSIT' : 'FULL',
          subscribeToNotifications: value.subscribeToNotifications,
        })

        await tryCreateAccount(value.buyerEmail)

        const checkout = result.checkout
        if (!checkout?.axeptaRedirectUrl) {
          throw new Error('No checkout redirect URL returned from server')
        }

        window.location.assign(checkout.axeptaRedirectUrl)
      } else {
        const paymentMethodMap: Record<string, PaymentMethod> = {
          'bank-transfer': 'BANK_TRANSFER',
          'cash': 'CASH',
          'floa': 'INSTALLMENTS',
          'vacation-cheque': 'VACATION_CHEQUE',
          'vacation-e-cheque': 'VACATION_E_CHEQUE',
        }

        const result = await initiateCheckout({
          buyerEmail: value.buyerEmail.trim(),
          buyerPhone: value.buyerPhone?.trim() || undefined,
          depositAmount: shouldPayDeposit ? value.depositAmount : undefined,
          paymentMethod: paymentMethodMap[value.paymentMethod]!,
          paymentType: shouldPayDeposit ? 'DEPOSIT' : 'FULL',
          subscribeToNotifications: value.subscribeToNotifications,
        })

        await tryCreateAccount(value.buyerEmail)

        // Non-CC flow: navigate client-side to the pending confirmation page
        const bookingReference = result.checkout?.bookingReference ?? ''
        void navigate({
          params: { status: 'pending' },
          search: { bookingRef: bookingReference, session: sessionId },
          to: '/payment-confirmation/$status',
        })
      }
    },
    validationLogic: revalidateLogic({ mode: 'submit', modeAfterSubmission: 'blur' }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate({ validationSource: 'form', value }, checkoutFormSchema),
    },
  })

  const handleVerifyPromoCode = async (code: string) => {
    if (!code.trim()) return
    const promoCodeData = await verifyPromoCode({ code })
    if (promoCodeData?.discount) {
      setPromoCodeDiscount(promoCodeData.discount)
      setPromoCodeError(false)
    } else {
      setPromoCodeError(true)
    }
  }

  const handleRemovePromoCode = () => {
    setPromoCodeDiscount(null)
    setPromoCodeError(false)
    form.setFieldValue('promoCode', '')
  }

  const handleBack = () => {
    void navigate({
      search: { session: sessionId },
      to: '/assurances',
    })
  }

  if (isLoading && !session) {
    return (
      <div className="flex w-full items-center justify-center py-20">
        <Loader2 className="text-brand-600 size-8 animate-spin" />
      </div>
    )
  }

  return (
    <>
      <form
        className="flex w-full flex-col gap-8 px-4 py-8 md:px-28 lg:flex-row lg:gap-12"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void form.handleSubmit()
        }}
      >
        {/* Mobile-only TripHeader */}
        {session && (
          <div className="lg:hidden">
            <TripHeader session={session} />
          </div>
        )}

        {/* Colonne droite — Sidebar */}
        <div className="w-full shrink-0 lg:order-last lg:w-96">
          <BookingSidebar
            promoCodeDiscount={promoCodeDiscount}
            promoCodeSlot={
              <CheckoutPromoCodeSlot
                fields={{ promoCode: 'promoCode' }}
                form={form}
                isVerifying={isPromoCodePending}
                onRemove={handleRemovePromoCode}
                onVerify={handleVerifyPromoCode}
                promoCodeDiscount={promoCodeDiscount}
                promoCodeError={promoCodeError}
              />
            }
            session={session}
          />
        </div>

        {/* Colonne gauche — Formulaire */}
        <div className="flex flex-1 flex-col gap-9">
          {/* Desktop-only TripHeader */}
          {session && (
            <div className="hidden lg:block">
              <TripHeader session={session} />
            </div>
          )}

          <div className="flex w-full flex-col gap-2">
            <div className="text-gray-light-900 text-lg font-semibold">
              Résumé des informations et paiement
            </div>
            <div className="text-gray-light-900 w-full text-sm">
              Vous avez sélectionné les services suivants pour votre voyage. Veuillez vérifier les
              informations avant de finaliser votre réservation.
            </div>
          </div>

          <div className="flex w-full flex-col gap-6">
            {/* Informations de contact + options compte */}
            <ContactInfoSection
              fields={{
                buyerEmail: 'buyerEmail',
                buyerPhone: 'buyerPhone',
                createAccount: 'createAccount',
                password: 'password',
                passwordConfirmation: 'passwordConfirmation',
                subscribeToNotifications: 'subscribeToNotifications',
              }}
              form={form}
            />

            {/* Type de paiement — Intégralité / Plusieurs fois */}
            {installmentAvailable && (
              <div className="border-brand-200 bg-brand-50/30 flex w-full flex-col gap-4 rounded-xl border p-4">
                <div className="text-brand-700 text-sm font-semibold">Type de paiement</div>
                <PaymentSection
                  fields={{
                    depositAmount: 'depositAmount',
                    isPayingInMultipleTimes: 'isPayingInMultipleTimes',
                  }}
                  form={form}
                  installmentAvailable={installmentAvailable}
                  minimumDeposit={minimumDeposit}
                  promoCodeDiscount={promoCodeDiscount}
                  totalPrice={totalPrice}
                />
              </div>
            )}

            {/* Mode de paiement — CB, virement, etc. */}
            <div className="border-gray-light-200 bg-gray-light-50/50 flex w-full flex-col gap-4 rounded-xl border p-4">
              <div className="flex flex-col gap-2">
                <div className="text-gray-light-900 text-sm font-semibold">Mode de paiement</div>
                <div className="text-gray-light-700 text-xs font-medium">
                  Tous les paiements sont sécurisés avec nos prestataires
                </div>
              </div>
              <PaymentMethodSelector
                fields={{ paymentMethod: 'paymentMethod' }}
                form={form}
                paymentDeadline={paymentDeadline}
                isFloaDisabled={isFloaDisabled}
                isReunion={isReunion}
              />
            </div>

            {/* Boutons de navigation */}
            <div className="flex w-full items-center justify-end gap-2">
              <Button
                className="hover:bg-gray-light-50 gap-2"
                onClick={handleBack}
                type="button"
                variant="none"
              >
                <ChevronLeft className="text-brand-700 h-4 w-4" />
                <div className="text-brand-700 text-sm font-medium">Étape précédente</div>
              </Button>
              <form.Subscribe
                selector={(state) => ({
                  isSubmitting: state.isSubmitting,
                  paymentMethod: state.values.paymentMethod,
                })}
              >
                {({ isSubmitting, paymentMethod }) => (
                  <Button
                    className="w-32 gap-2"
                    disabled={isSubmitting || isCheckoutPending}
                    type="submit"
                  >
                    {isSubmitting || isCheckoutPending ? (
                      <Loader2 className="h-4 w-4 animate-spin text-white" />
                    ) : (
                      <div className="text-sm font-medium text-white">
                        {paymentMethod === 'credit-card' ? 'Payer' : 'Réserver'}
                      </div>
                    )}
                  </Button>
                )}
              </form.Subscribe>
            </div>
          </div>
        </div>
      </form>
    </>
  )
}
